/**
 * Verification panel — OSINT source verification checklist.
 *
 * Activated when an event is selected (event:selected). Displays
 * a checklist for the selected event with toggleable checkboxes.
 * State stored per-event in memory. Shows source authority tier
 * and related events for cross-reference.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import type { NormalizedEvent } from '@/sources/base-source';
import { relativeTime } from '@/utils/time';
import { haversineDistance } from '@/utils/geo';
import { getAuthorityTier, getTierLabel } from '@/config/authority';

const CHECKLIST_ITEMS = [
  'Source identified and attributed',
  'Multiple sources corroborate',
  'Temporal consistency (timestamps make sense)',
  'Geographic plausibility (location matches description)',
  'No contradictory evidence found',
  'Authority tier assessed',
] as const;

export function createVerificationPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'verification-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];

  // Store verification state per event ID (capped to prevent unbounded growth)
  const MAX_VERIFICATION_ENTRIES = 200;
  const verificationState = new Map<string, boolean[]>();

  function getEventState(eventId: string): boolean[] {
    if (!verificationState.has(eventId)) {
      // Evict oldest entry if at capacity
      if (verificationState.size >= MAX_VERIFICATION_ENTRIES) {
        const oldest = verificationState.keys().next().value;
        if (oldest !== undefined) verificationState.delete(oldest);
      }
      verificationState.set(eventId, new Array(CHECKLIST_ITEMS.length).fill(false));
    }
    return verificationState.get(eventId)!;
  }

  function renderEmpty(): void {
    contentArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
    msg.textContent = 'Select an event to verify';
    contentArea.appendChild(msg);
  }

  function render(event: NormalizedEvent): void {
    contentArea.textContent = '';

    // --- Header ---
    const header = document.createElement('div');
    header.style.cssText = 'margin-bottom: 12px;';

    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0; font-size: 13px; font-weight: 600; color: var(--text-primary); line-height: 1.3;';
    title.textContent = event.title;
    header.appendChild(title);

    const meta = document.createElement('div');
    meta.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 4px;';
    meta.textContent = `${event.source} \u00B7 ${relativeTime(event.timestamp)}`;
    header.appendChild(meta);

    contentArea.appendChild(header);

    // --- Authority Tier ---
    const tierSection = document.createElement('div');
    tierSection.style.cssText = 'margin-bottom: 12px;';

    const tierHeader = document.createElement('div');
    tierHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em;';
    tierHeader.textContent = 'Source Authority';
    tierSection.appendChild(tierHeader);

    const tier = getAuthorityTier(event.source);
    const tierLabel = getTierLabel(tier);

    const tierBadge = document.createElement('div');
    tierBadge.style.cssText = `
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 10px; border-radius: 4px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const tierNum = document.createElement('span');
    tierNum.style.cssText = `font-size: 14px; font-weight: 700; font-family: var(--font-mono); color: ${tier === 1 ? 'var(--accent)' : tier === 2 ? 'var(--text-primary)' : 'var(--text-muted)'};`;
    tierNum.textContent = `T${tier}`;
    tierBadge.appendChild(tierNum);

    const tierName = document.createElement('span');
    tierName.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    tierName.textContent = tierLabel;
    tierBadge.appendChild(tierName);

    tierSection.appendChild(tierBadge);
    contentArea.appendChild(tierSection);

    // --- Checklist ---
    const checkSection = document.createElement('div');
    checkSection.style.cssText = 'margin-bottom: 16px;';

    const checkHeader = document.createElement('div');
    checkHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    checkHeader.textContent = 'Verification Checklist';
    checkSection.appendChild(checkHeader);

    const state = getEventState(event.id);
    const checkedCount = state.filter(Boolean).length;

    const progress = document.createElement('div');
    progress.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-bottom: 6px;';
    progress.textContent = `${checkedCount}/${CHECKLIST_ITEMS.length} verified`;
    checkSection.appendChild(progress);

    for (let i = 0; i < CHECKLIST_ITEMS.length; i++) {
      const row = document.createElement('label');
      row.style.cssText = `
        display: flex; align-items: center; gap: 8px; padding: 4px 8px;
        margin-bottom: 2px; border-radius: 4px; cursor: pointer;
        background: var(--bg-secondary);
      `;

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = state[i];
      checkbox.style.cssText = 'flex-shrink: 0; accent-color: var(--accent);';
      checkbox.onchange = () => {
        state[i] = checkbox.checked;
        const count = state.filter(Boolean).length;
        progress.textContent = `${count}/${CHECKLIST_ITEMS.length} verified`;
      };
      row.appendChild(checkbox);

      const labelText = document.createElement('span');
      labelText.style.cssText = `font-size: 11px; color: ${state[i] ? 'var(--text-primary)' : 'var(--text-secondary)'};`;
      labelText.textContent = CHECKLIST_ITEMS[i];
      row.appendChild(labelText);

      checkSection.appendChild(row);
    }

    contentArea.appendChild(checkSection);

    // --- Related Events for Cross-Reference ---
    if (event.location) {
      const relatedSection = document.createElement('div');

      const relatedHeader = document.createElement('div');
      relatedHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
      relatedHeader.textContent = 'Nearby Events (Cross-Reference)';
      relatedSection.appendChild(relatedHeader);

      const allEvents = appStore.getState().events;
      const loc = event.location;
      const dayMs = 24 * 60 * 60 * 1000;
      const eventTime = new Date(event.timestamp).getTime();

      const related = allEvents.filter(e => {
        if (e.id === event.id) return false;
        if (!e.location) return false;
        const timeDiff = Math.abs(new Date(e.timestamp).getTime() - eventTime);
        if (timeDiff > dayMs) return false;
        return haversineDistance(loc.lat, loc.lng, e.location.lat, e.location.lng) <= 50;
      });

      related.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const display = related.slice(0, 8);

      if (display.length === 0) {
        const noRelated = document.createElement('div');
        noRelated.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
        noRelated.textContent = 'No nearby events for cross-reference';
        relatedSection.appendChild(noRelated);
      } else {
        for (const rel of display) {
          const relRow = document.createElement('div');
          relRow.style.cssText = `
            padding: 4px 8px; margin-bottom: 3px; border-radius: 4px;
            background: var(--bg-secondary); cursor: pointer;
          `;
          relRow.onclick = () => eventBus.emit('event:selected', rel);

          const relTitle = document.createElement('div');
          relTitle.style.cssText = 'font-size: 11px; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
          relTitle.textContent = rel.title;
          relRow.appendChild(relTitle);

          const relMeta = document.createElement('div');
          relMeta.style.cssText = 'font-size: 10px; color: var(--text-muted);';
          relMeta.textContent = `${rel.source} \u00B7 ${relativeTime(rel.timestamp)}`;
          relRow.appendChild(relMeta);

          relatedSection.appendChild(relRow);
        }
      }

      contentArea.appendChild(relatedSection);
    }
  }

  // Initial empty state
  renderEmpty();

  // Listen for event selection
  cleanupFns.push(
    eventBus.on('event:selected', (event) => render(event)),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
